/**
 * i18n.js — Uzbek-Latin copy used across screens.
 *
 * The backend already returns display strings for hours (`8s 45m`),
 * month_label (`Iyul 2026`), and weekday text (`shanba` / `Sh`), so this file
 * carries ONLY the client-owned copy: greetings, button labels, status words,
 * section headers, and a couple of local formatters. Screens import `T` and,
 * where a value depends on time-of-day or a name, call the helper fns.
 */

export const T = {
  // ---- app chrome / titles --------------------------------------------------
  app_title: 'Davomat',
  title_location: 'Joylashuv',
  title_face: 'Yuzni tekshirish',
  title_consent: 'Rozilik',
  title_branch: 'Filial',
  title_history: 'Mening davomatim',

  // ---- greetings ------------------------------------------------------------
  greet_hello: 'Assalomu alaykum',           // + ", <Name>! 👋"
  greet_thanks: 'Rahmat',                     // + ", <Name>! 🌙"
  greet_morning: 'Xayrli tong',
  greet_day_ongoing: 'Ish kuni davom etmoqda',
  greet_day_done: 'Bugungi ish yakunlandi',

  // ---- landing status card --------------------------------------------------
  status_waiting_pill: 'Kutilyapti',
  status_in_pill: 'Kirgan',
  status_done_pill: 'Yakunlandi',
  status_not_in_yet: 'Bugun hali kirmagansiz',
  status_at_work: 'Ishdasiz ✅',
  status_day_over: 'Ish kuni tugadi',
  label_branch: 'Filial',
  label_checkin: 'Kirish',
  label_checkout: 'Chiqish',
  label_now: 'Hozir',
  label_total: 'Jami',

  // ---- main / secondary buttons ---------------------------------------------
  btn_checkin: 'Kirish (Check-in)',
  btn_checkout: 'Chiqish (Check-out)',
  btn_day_done: 'Bugun tugadi',
  btn_my_attendance: '📅 Mening davomatim',
  btn_continue: 'Davom etish',
  btn_recheck: 'Qayta tekshirish',
  btn_retry: 'Qayta urinish',
  btn_back_home: 'Bosh sahifaga qaytish',
  btn_open_settings: 'Sozlamalarni ochish',
  btn_change_branch: 'Filialni o‘zgartirish',
  btn_call_manager: 'Menejerga murojaat',
  btn_pair: 'Ulash',
  btn_consent_accept: 'Roziman, davom etish',
  btn_consent_decline: 'Rad etish',
  btn_capture: 'Rasmga olish',
  btn_close: 'Yopish',

  // ---- pairing --------------------------------------------------------------
  pair_title: 'Hisobni ulash',
  pair_prompt: 'Sizga berilgan PIN kodni kiriting.',
  pair_pin_placeholder: 'PIN kod',

  // ---- consent --------------------------------------------------------------
  consent_h: 'Yuz orqali tasdiqlashga rozilik',
  consent_b:
    'Davomatni tasdiqlash uchun ilova yuzingiz tasviridan foydalanadi. ' +
    'Bu faqat kirish va chiqishni tekshirish uchun.',
  consent_pill_only: '📷 Faqat davomat uchun',
  consent_pill_enc: '🔒 Shifrlangan saqlanadi',
  consent_pill_third: '🚫 Uchinchi shaxsga berilmaydi',

  // ---- enrolment ------------------------------------------------------------
  enroll_h: 'Yuzni ro‘yxatdan o‘tkazish',
  enroll_b: 'Yuzingizni bir marta rasmga oling — keyingi safar shu bo‘yicha tanib olamiz.',

  // ---- branch pick ----------------------------------------------------------
  branch_prompt: 'Qaysi filialdasiz?',
  branch_selected: 'Tanlangan',

  // ---- location -------------------------------------------------------------
  loc_checking: 'Joylashuv tekshirilmoqda…',
  loc_at_branch: 'filialidasiz',              // "<Branch> filialidasiz"
  loc_confirmed: 'Joylashuv tasdiqlandi. Endi yuzingizni tekshiramiz.',
  loc_far_h: 'Filialdan uzoqdasiz',
  loc_far_b: 'Kirish uchun filial radiusi ichida bo‘lishingiz kerak.',

  // ---- face -----------------------------------------------------------------
  face_privacy: '🔒 Rasm faqat davomat uchun ishlatiladi',
  // Four server-issued liveness cues. The tokens (turn_left/turn_right/look_up/
  // look_down) MUST match the Odoo VERIFIABLE_CUES and the sidecar exactly.
  cue_turn_left: 'Boshingizni chapga buring ←',
  cue_turn_right: 'Boshingizni o‘ngga buring →',
  cue_look_up: 'Boshingizni tepaga ko‘taring ↑',
  cue_look_down: 'Boshingizni pastga egdiring ↓',
  cue_expires_in: 'Amal qiladi',              // "Amal qiladi: 42s"
  cue_stale_h: 'Vaqt tugadi',
  cue_stale_b: 'Yangi ko‘rsatma oling va qaytadan suratga oling.',
  btn_new_cue: 'Yangi ko‘rsatma',

  // ---- result ---------------------------------------------------------------
  result_in_h: 'Kirdingiz!',
  result_out_h: 'Chiqdingiz!',
  result_in_wish: 'Ishingizga omad! 💪',
  result_out_wish: 'Yaxshi dam oling! 🌙',

  // ---- history / self-service ----------------------------------------------
  tile_week: 'Bu hafta',
  tile_month: 'Bu oy',
  tile_workdays: 'Ish kunlari',
  unit_days: 'kun',
  live_now: 'Hozir ishda',                    // "Hozir ishda · 3s 20m"
  legend_present: 'Ishlangan',
  legend_open: 'Ochiq',
  legend_absent: 'Kelmagan',
  legend_rest: 'Dam',
  legend_flag: 'Kech/Erta',
  empty_month_h: 'Bu oyda davomat yo‘q',
  empty_month_b:
    'Bu oy uchun kirish/chiqish topilmadi. Boshqa oyni ko‘rish uchun ‹ › tugmalardan foydalaning.',
  day_late: 'kech',                           // "kech 30 daq"
  day_early: 'erta',                          // "erta 50 daq"
  unit_min: 'daq',
  day_status_present: 'Ishlangan',
  day_status_open: 'Ochiq',
  day_status_absent: 'Kelmagan',
  day_status_rest: 'Dam',

  // ---- browser capture page (/capture) --------------------------------------
  cap_getting_location: 'Joylashuv aniqlanmoqda…',
  cap_starting_camera: 'Kamera ishga tushmoqda…',
  cap_loading_face: 'Yuz tekshiruvi yuklanmoqda…',
  cap_align: 'Yuzingizni oval ichiga joylashtiring',
  cap_hold_still: 'Qimirlamang…',
  cap_capturing: 'Suratga olinmoqda…',
  cap_sending: 'Yuborilmoqda…',
  cap_follow_cue: 'Ko‘rsatma bo‘yicha boshingizni buring',
  cap_success_h: 'Davomat qabul qilindi',
  cap_success_in: 'Kirish qayd etildi ✅',
  cap_success_out: 'Chiqish qayd etildi 🌙',
  btn_back_to_tg: 'Telegramga qaytish',

  // ---- in-app live capture (InAppLiveCapture) -------------------------------
  live_starting_cam: 'Kamera ishga tushmoqda…',
  live_loading_face: 'Yuz tekshiruvi yuklanmoqda…',
  // placement guidance (one shown at a time, driven by facePlacement reason)
  live_show_face: 'Yuzingizni ko‘rsating',
  live_come_closer: 'Yaqinroq keling',
  live_move_back: 'Biroz uzoqlashing',
  live_center_face: 'Yuzingizni doira ichida joylashtiring',
  // action cues (Phase 1 — the worker performs one before the countdown)
  act_smile: 'Jilmayng 😊',
  act_wink_left: 'Chap ko‘zingizni yuming',
  act_wink_right: 'O‘ng ko‘zingizni yuming',
  act_still: 'Qimirlamang',
  live_hold: 'Ushlab turing…',
  live_ready: 'Tayyor…',                      // Phase 2 countdown lead-in
  live_capturing: 'Suratga olinmoqda…',
  live_sending: 'Yuborilmoqda…',
  live_opening_browser: 'Brauzer orqali ochilmoqda…',

  // ---- not-linked (bot onboarding) notice -----------------------------------
  // Shown instead of the retired PIN screen when /session -> NOT_PAIRED: the
  // worker must link in the BOT (share phone) before the Mini App resolves them.
  notlinked_title: 'Ro‘yxatdan o‘tish',
  notlinked_h: 'Botda ro‘yxatdan o‘ting',
  notlinked_b:
    'Davomatni boshlash uchun avval botda ro‘yxatdan o‘ting. Bu bir martalik amal.',
  notlinked_step1: '1. Botni oching va “Boshlash”ni bosing',
  notlinked_step2: '2. “Telefonni ulashish” tugmasini bosing',
  notlinked_step3: '3. So‘ng shu ilovani qaytadan oching',
  notlinked_once: '🔒 Raqamingiz faqat sizni xodim sifatida tanish uchun — bir marta',
  btn_open_bot: 'Botni ochish',

  // ---- generic --------------------------------------------------------------
  loading: 'Yuklanmoqda…',
  step_of: '/',                               // "1 / 2"
};

// Monday-first Uzbek weekday headers for the calendar grid (Du..Ya).
export const WEEKDAY_SHORT = ['Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh', 'Ya'];

// Uzbek month names (1-indexed at [1]); the backend also sends month_label,
// but the pager may need a bare name locally.
export const MONTH_UZ = [
  '', 'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
  'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr',
];

/** Time-of-day greeting line (uses local device hour). */
export function greetingSub(hour = new Date().getHours()) {
  if (hour < 12) return T.greet_morning;
  if (hour < 18) return T.greet_day_ongoing;
  return T.greet_day_ongoing;
}

/** "Assalomu alaykum, Javohir! 👋" */
export function greetHello(name) {
  return `${T.greet_hello}, ${name || ''}! 👋`;
}

/** "Rahmat, Javohir! 🌙" — end-of-day greeting. */
export function greetThanks(name) {
  return `${T.greet_thanks}, ${name || ''}! 🌙`;
}

/** ISO datetime -> "HH:MM" in the string's own offset (server sends +05:00). */
export function hhmm(iso) {
  if (!iso) return '';
  // The backend sends "...T09:03:00+05:00"; slice the clock straight out so we
  // never re-interpret it in the device timezone.
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[1]}:${m[2]}` : '';
}

/** Map a server cue token to its displayed Uzbek instruction. */
export function cueText(cue) {
  if (cue === 'turn_left') return T.cue_turn_left;
  if (cue === 'turn_right') return T.cue_turn_right;
  if (cue === 'look_up') return T.cue_look_up;
  if (cue === 'look_down') return T.cue_look_down;
  return '';
}

/** Big standalone direction arrow for a cue token (← → ↑ ↓). */
export function cueArrow(cue) {
  if (cue === 'turn_left') return '←';
  if (cue === 'turn_right') return '→';
  if (cue === 'look_up') return '↑';
  if (cue === 'look_down') return '↓';
  return '';
}

/** In-app placement-guidance text for a facePlacement() reason. */
export function placementText(reason) {
  if (reason === 'small') return T.live_come_closer;
  if (reason === 'big') return T.live_move_back;
  if (reason === 'offcenter') return T.live_center_face;
  return T.live_show_face; // 'none' / unknown
}

/** In-app action-cue text for an action token (smile/wink_left/wink_right/still). */
export function actionText(action) {
  if (action === 'smile') return T.act_smile;
  if (action === 'wink_left') return T.act_wink_left;
  if (action === 'wink_right') return T.act_wink_right;
  if (action === 'still') return T.act_still;
  return '';
}

export default T;
